
import { GoogleGenAI, GenerateContentResponse } from "@google/genai";

const ai = process.env.API_KEY ? new GoogleGenAI({ apiKey: process.env.API_KEY }) : null;

const INLINE_SIZE_LIMIT = 20 * 1024 * 1024; // 20 MB

const fileToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      const result = reader.result as string;
      const base64Data = result.split(',')[1];
      resolve(base64Data);
    };
    reader.onerror = (error) => reject(error);
  });
};

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

/** Upload file via Gemini File API, poll until ACTIVE, return the file URI and name. */
const uploadViaFileApi = async (file: File): Promise<{ uri: string; name: string }> => {
    if (!ai) throw new Error("API not initialised");

    const uploadedFile = await ai.files.upload({
        file,
        config: { mimeType: file.type, displayName: file.name },
    });

    let fileInfo = uploadedFile;
    // poll until ACTIVE
    while (fileInfo.state !== 'ACTIVE') {
        if (fileInfo.state === 'FAILED') {
            throw new Error(`Gemini File API processing failed for: ${file.name}`);
        }
        await sleep(2000); // poll every 2s
        fileInfo = await ai.files.get({ name: fileInfo.name! });
    }

    return { uri: fileInfo.uri!, name: fileInfo.name! };
};

const buildPrompt = (diarization: boolean): string => {
    const base =
        "Transcribe the speech from this file. Format the output with timestamps in square brackets like [MM:SS] or [HH:MM:SS] followed by the text for that segment. Start a new line for every new timestamp. Provide only the timestamped transcript.";

    if (!diarization) return base;

    return (
        base +
        " Additionally, identify different speakers and label each segment with a speaker tag such as 'Speaker A:', 'Speaker B:', etc. Use consistent labels throughout so the same speaker always gets the same label. Perform speaker diarization to distinguish between speakers."
    );
};

export const transcribeMedia = async (file: File, diarization = false): Promise<string> => {
    if (!ai) {
        throw new Error("Transcription is unavailable: API_KEY is not configured.");
    }
    try {
        const textPart = { text: buildPrompt(diarization) };
        let mediaPart: object;
        let uploadedFileName: string | null = null;

        if (file.size > INLINE_SIZE_LIMIT) {
            // Large file: use Gemini File API upload → poll → transcribe → delete
            const { uri, name } = await uploadViaFileApi(file);
            uploadedFileName = name;
            mediaPart = {
                fileData: {
                    mimeType: file.type,
                    fileUri: uri,
                },
            };
        } else {
            // Small file: inline base64
            const base64Data = await fileToBase64(file);
            mediaPart = {
                inlineData: {
                    mimeType: file.type,
                    data: base64Data,
                },
            };
        }

        let transcription: string | undefined;
        try {
            const response: GenerateContentResponse = await ai.models.generateContent({
                model: 'gemini-2.5-flash',
                contents: { parts: [mediaPart, textPart] },
            });
            transcription = response.text;
        } finally {
            // Always clean up the uploaded file from Gemini File API
            if (uploadedFileName) {
                try {
                    await ai.files.delete({ name: uploadedFileName });
                } catch (deleteErr) {
                    console.warn("Failed to delete uploaded file from Gemini File API:", deleteErr);
                }
            }
        }

        if (!transcription) {
            throw new Error("The API returned an empty transcription. The media may be silent or unsupported.");
        }

        return transcription;

    } catch (error) {
        console.error("Error during transcription:", error);

        let errorMessage = "An unknown error occurred during transcription.";

        if (error instanceof Error) {
            errorMessage = error.message;
        } else if (typeof error === 'object' && error !== null) {
            const apiError = error as any;
            if (apiError.error?.message) {
                errorMessage = apiError.error.message;
            } else if (apiError.message) {
                errorMessage = apiError.message;
            } else {
                try {
                    errorMessage = JSON.stringify(error);
                } catch {
                    errorMessage = "Could not process the error object.";
                }
            }
        }

        throw new Error(`Failed to transcribe media. ${errorMessage}`);
    }
};
